import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [initializing, setInitializing] = useState(true);
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [kits, setKits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);

  const loadAll = useCallback(async () => {
    const [i, k, p, c] = await Promise.all([
      api.listItems(),
      api.listKits(),
      api.listProjects(),
      api.listCategories(),
    ]);
    setItems(i);
    setKits(k);
    setProjects(p);
    setCategories(c);
  }, []);

  // Bootstrap: try to restore session
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('wgi_token');
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        const u = await api.me();
        setUser(u);
        await loadAll();
      } catch (e) {
        localStorage.removeItem('wgi_token');
      } finally {
        setInitializing(false);
      }
    };
    init();
  }, [loadAll]);

  // ---- Auth ----
  const login = async (email, password) => {
    try {
      const r = await api.login(email, password);
      localStorage.setItem('wgi_token', r.accessToken);
      setUser(r.user);
      await loadAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('wgi_token');
    setUser(null);
    setItems([]);
    setKits([]);
    setProjects([]);
  };

  // ---- Derived helpers (client-side) ----
  const expandProjectGear = useCallback(
    (project) => {
      if (!project) return [];
      const counts = new Map();
      const bump = (code, qty, source) => {
        const cur = counts.get(code) || { qty: 0, sources: new Set() };
        cur.qty += qty;
        cur.sources.add(source);
        counts.set(code, cur);
      };
      (project.itemCodes || []).forEach((c) => bump(c, 1, 'direct'));
      (project.kitCodes || []).forEach((kc) => {
        const kit = kits.find((k) => k.code === kc);
        if (!kit) return;
        (kit.itemCodes || []).forEach((ic) => bump(ic, 1, `kit:${kc}`));
      });
      return Array.from(counts.entries()).map(([itemCode, v]) => ({
        itemCode,
        qty: v.qty,
        sources: Array.from(v.sources),
      }));
    },
    [kits],
  );

  const projectProgress = useCallback(
    (project) => {
      const expanded = expandProjectGear(project);
      const totalUnits = expanded.reduce((s, e) => s + e.qty, 0);
      const packedUnits = expanded.reduce(
        (s, e) => s + Math.min(e.qty, project?.packed?.[e.itemCode] || 0),
        0,
      );
      return { totalUnits, packedUnits, expanded };
    },
    [expandProjectGear],
  );

  const checkedOutByCode = useMemo(() => {
    const map = {};
    projects
      .filter((p) => p.status === 'active')
      .forEach((p) => {
        const packed = p.packed || {};
        Object.entries(packed).forEach(([code, qty]) => {
          map[code] = (map[code] || 0) + qty;
        });
      });
    return map;
  }, [projects]);

  // Expanded reservation per code, summed across all active projects (direct items + items cascaded from kits)
  const reservedByCode = useMemo(() => {
    const out = {};
    projects
      .filter((p) => p.status === 'active')
      .forEach((p) => {
        (p.itemCodes || []).forEach((c) => {
          out[c] = (out[c] || 0) + 1;
        });
        (p.kitCodes || []).forEach((kc) => {
          const kit = kits.find((k) => k.code === kc);
          if (!kit) return;
          (kit.itemCodes || []).forEach((ic) => {
            out[ic] = (out[ic] || 0) + 1;
          });
        });
      });
    return out;
  }, [projects, kits]);

  // Per-code reservation breakdown: which active project holds how many units (direct + via kits)
  const reservationBreakdownByCode = useCallback(
    (code) => {
      const rows = [];
      projects
        .filter((p) => p.status === 'active')
        .forEach((p) => {
          let qty = 0;
          const direct = (p.itemCodes || []).filter((c) => c === code).length;
          if (direct) qty += direct;
          const viaKits = [];
          (p.kitCodes || []).forEach((kc) => {
            const kit = kits.find((k) => k.code === kc);
            if (!kit) return;
            const n = (kit.itemCodes || []).filter((ic) => ic === code).length;
            if (n) {
              qty += n;
              viaKits.push({ kitCode: kit.code, kitName: kit.name, qty: n });
            }
          });
          if (qty > 0) {
            rows.push({
              projectId: p.id,
              projectName: p.name,
              direct,
              viaKits,
              qty,
            });
          }
        });
      return rows;
    },
    [projects, kits],
  );

  const itemAvailableQty = useCallback(
    (code, opts = {}) => {
      const it = items.find((i) => i.code === code);
      if (!it) return 0;
      // Default: subtract everything reserved on active projects
      // (intra-project double-booking + cross-project conflicts).
      // If excludeProjectId is given, ignore that project's reservation so the
      // Add Gear dialog can show what *would* still fit if removed/re-added.
      let reserved = reservedByCode[code] || 0;
      if (opts.excludeProjectId) {
        const p = projects.find((pp) => pp.id === opts.excludeProjectId);
        if (p && p.status === 'active') {
          let onSelf = 0;
          (p.itemCodes || []).forEach((c) => {
            if (c === code) onSelf += 1;
          });
          (p.kitCodes || []).forEach((kc) => {
            const kit = kits.find((k) => k.code === kc);
            if (!kit) return;
            (kit.itemCodes || []).forEach((ic) => {
              if (ic === code) onSelf += 1;
            });
          });
          reserved -= onSelf;
        }
      }
      return Math.max(0, it.totalQty - reserved);
    },
    [items, projects, kits, reservedByCode],
  );

  const itemStatus = useCallback(
    (code) => {
      const it = items.find((i) => i.code === code);
      if (!it) return 'unknown';
      const packed = checkedOutByCode[code] || 0;
      const reserved = reservedByCode[code] || 0;
      if (packed >= it.totalQty) return 'checked-out';
      if (packed > 0) return 'partial';
      if (reserved > 0) return 'reserved';
      return 'available';
    },
    [items, checkedOutByCode, reservedByCode],
  );

  // Aggregate status for a kit, derived from its contained items.
  // Returns the most "blocking" state present + counts for tooltip use.
  const kitStatus = useCallback(
    (kit) => {
      if (!kit || !(kit.itemCodes || []).length) {
        return { status: 'available', blocked: 0, total: 0 };
      }
      const uniqueCodes = Array.from(new Set(kit.itemCodes));
      let worst = 'available';
      let blocked = 0;
      const rank = { available: 0, reserved: 1, partial: 2, 'checked-out': 3 };
      for (const code of uniqueCodes) {
        const s = itemStatus(code);
        if (s !== 'available' && s !== 'unknown') blocked += 1;
        if ((rank[s] || 0) > (rank[worst] || 0)) worst = s;
      }
      return { status: worst, blocked, total: uniqueCodes.length };
    },
    [itemStatus],
  );

  const replaceProject = (proj) => {
    setProjects((prev) => {
      const idx = prev.findIndex((p) => p.id === proj.id);
      if (idx === -1) return [proj, ...prev];
      const next = [...prev];
      next[idx] = proj;
      return next;
    });
  };

  // ---- Projects ----
  const createProject = async (data) => {
    const p = await api.createProject(data);
    setProjects((prev) => [p, ...prev]);
    return p;
  };
  const updateProject = async (id, patch) => {
    const p = await api.updateProject(id, patch);
    replaceProject(p);
    return p;
  };
  const deleteProject = async (id) => {
    await api.deleteProject(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };
  const completeProject = async (id) => {
    const p = await api.completeProject(id);
    replaceProject(p);
    return p;
  };
  const reactivateProject = async (id) => {
    const p = await api.reactivateProject(id);
    replaceProject(p);
    return p;
  };
  const unlinkProjectFromPm = async (id) => {
    const p = await api.unlinkProjectFromPm(id);
    replaceProject(p);
    return p;
  };

  const projectAddItem = async (id, code) => {
    const p = await api.addItemToProject(id, code);
    replaceProject(p);
  };
  const projectRemoveItem = async (id, code) => {
    const p = await api.removeItemFromProject(id, code);
    replaceProject(p);
  };
  const projectAddKit = async (id, code) => {
    const p = await api.addKitToProject(id, code);
    replaceProject(p);
  };
  const projectRemoveKit = async (id, code) => {
    const p = await api.removeKitFromProject(id, code);
    replaceProject(p);
  };
  const projectClearGear = async (id) => {
    const p = await api.clearProjectGear(id);
    replaceProject(p);
  };

  const projectReturnAll = async (id) => {
    const p = await api.returnAllPacked(id);
    replaceProject(p);
    // Media items may have been transitioned to 'Needs Offload' — refresh items
    // so the Media kanban reflects it immediately.
    api.listItems().then(setItems).catch(() => {});
    return p;
  };

  // ---- Scan (used by ScanInput) ----
  const scanForProject = async (projectId, code, direction) => {
    try {
      const r = await api.scanProject(projectId, code, direction);
      if (r.project) replaceProject(r.project);
      // A scan can auto-transition media_status on the backend (pack → 'In Use',
      // return → 'Needs Offload'), for the scanned item AND for kit-cascaded
      // items. Re-fetch the items list so the Media Management kanban + Add
      // Gear dialog badges reflect the change without a manual refresh.
      const codesTouched = [code, ...((r.results || []).map((x) => x.itemCode))];
      const anyMedia = codesTouched.some((c) =>
        items.some((it) => it.code === c && it.category === 'Media'),
      );
      if (anyMedia) {
        api.listItems().then(setItems).catch(() => {
          /* best effort — page still works with stale data */
        });
      }
      // Normalize for ScanInput's success-phrasing logic
      let itemOut = null;
      let kitOut = null;
      if (r.kind === 'item' && r.itemName) {
        itemOut = { name: r.itemName, code };
      }
      if (r.kind === 'kit' && r.kitName) {
        const localKit = kits.find((k) => k.code === code || k.qrCode === code);
        kitOut = {
          code: localKit?.code || code,
          name: r.kitName,
          itemCodes: localKit?.itemCodes || [],
        };
      }
      return {
        ok: r.ok,
        item: itemOut,
        kit: kitOut,
        results: r.results,
        message: r.message,
        error: r.ok ? null : r.message,
      };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  // ---- Items / Kits CRUD ----
  const addItem = async (data) => {
    const it = await api.createItem(data);
    setItems((prev) => [...prev, it]);
    return it;
  };
  const updateItem = async (code, patch) => {
    const it = await api.updateItem(code, patch);
    setItems((prev) => prev.map((x) => (x.code === code ? it : x)));
    return it;
  };
  const deleteItem = async (code) => {
    await api.deleteItem(code);
    setItems((prev) => prev.filter((x) => x.code !== code));
  };

  const addKit = async (data) => {
    const k = await api.createKit(data);
    setKits((prev) => [...prev, k]);
    return k;
  };
  const updateKit = async (code, patch) => {
    const k = await api.updateKit(code, patch);
    setKits((prev) => prev.map((x) => (x.code === code ? k : x)));
    return k;
  };
  const deleteKit = async (code) => {
    await api.deleteKit(code);
    setKits((prev) => prev.filter((x) => x.code !== code));
  };

  // ---- Categories CRUD ----
  const addCategory = async (data) => {
    const c = await api.createCategory(data);
    setCategories((prev) => [...prev, c]);
    return c;
  };
  const updateCategory = async (id, patch) => {
    const c = await api.updateCategory(id, patch);
    setCategories((prev) => prev.map((x) => (x.id === id ? c : x)));
    // If the category was renamed, propagate the change to any items in
    // memory so the UI updates without a full refetch.
    if (patch.name) {
      const prev = categories.find((x) => x.id === id);
      if (prev && prev.name !== c.name) {
        setItems((all) =>
          all.map((it) => (it.category === prev.name ? { ...it, category: c.name } : it)),
        );
      }
    }
    return c;
  };
  const deleteCategory = async (id) => {
    await api.deleteCategory(id);
    setCategories((prev) => prev.filter((x) => x.id !== id));
  };
  const reorderCategories = async (ids) => {    // Optimistically update local order so the drag feels instant.
    setCategories((prev) => {
      const map = new Map(prev.map((c) => [c.id, c]));
      const reordered = ids
        .map((id, idx) => (map.has(id) ? { ...map.get(id), sortOrder: idx } : null))
        .filter(Boolean);
      // Append anything not in `ids` (defensive — shouldn't happen)
      const seen = new Set(ids);
      prev.forEach((c) => {
        if (!seen.has(c.id)) reordered.push(c);
      });
      return reordered;
    });
    try {
      await api.reorderCategories(ids);
    } catch (e) {
      // Roll back on failure by re-fetching
      const fresh = await api.listCategories();
      setCategories(fresh);
      throw e;
    }
  };
  const categoryColor = useCallback(
    (name) => categories.find((c) => c.name === name)?.color || '#737373',
    [categories],
  );

  // ---- Media ----
  const setMediaStatus = async (code, status) => {
    const updated = await api.setMediaStatus(code, status);
    // Patch the item in local state so kanban + inventory reflect it instantly
    setItems((prev) => prev.map((it) => (it.code === code ? { ...it, ...updated } : it)));
    return updated;
  };

  const value = {
    initializing,
    user,
    login,
    logout,

    items,
    kits,
    projects,
    categories,
    categoryColor,
    refreshProjects: loadAll,

    checkedOutByCode,
    itemAvailableQty,
    reservationBreakdownByCode,
    itemStatus,
    kitStatus,
    expandProjectGear,
    projectProgress,

    createProject,
    updateProject,
    deleteProject,
    completeProject,
    reactivateProject,
    unlinkProjectFromPm,
    projectAddItem,
    projectRemoveItem,
    projectAddKit,
    projectRemoveKit,
    projectClearGear,
    projectReturnAll,
    scanForProject,

    addItem,
    updateItem,
    deleteItem,
    addKit,
    updateKit,
    deleteKit,

    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,

    setMediaStatus,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
